import { Clock, Check, X, Calendar, Users, MapPin, DollarSign, Tag } from 'lucide-react';
import { useTranslation } from '../../../i18n/i18n';

const ActivityView = ({ product, lang }) => {
    const { t } = useTranslation(lang);

    const parseLanguages = (languagesString) => {
        if (!languagesString || languagesString === "null") return [];
        try {
            return JSON.parse(languagesString);
        } catch {
            return [];
        }
    };

    const getFieldValue = (fieldArray, fallback = '') => {
        if (!fieldArray || fieldArray.length === 0) return fallback;
        const value = fieldArray[0][lang] || fieldArray[0].en || fieldArray[0].fr || fieldArray[0].es;
        return value || fallback;
    };

    const getActivityName = () => {
        if (!product.activity) return '';
        return product.activity[lang] || product.activity.en || product.activity.fr || product.activity.es || '';
    };

    const formatDuration = (value, unit) => {
        if (!value || value === 0) return t('notSpecified');
        const unitTranslated = t(unit.toLowerCase());
        return `${value} ${unitTranslated}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return t('notSpecified');
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US');
        } catch {
            return dateString;
        }
    };

    const getPricingSummary = () => {
        if (!product.activity?.pricing || product.activity.pricing.length === 0) {
            return t('noPricing');
        }

        // Filtrer les prix valides et prendre le prix adulte comme référence
        const adultPrices = product.activity.pricing
            .map(p => p.adult_price)
            .filter(price => price != null && !isNaN(price) && price > 0);

        if (adultPrices.length === 0) {
            return t('noPricing');
        }

        const minPrice = Math.min(...adultPrices);
        const maxPrice = Math.max(...adultPrices);

        if (minPrice === maxPrice) {
            return `${minPrice}€`;
        }
        return `${minPrice}€ - ${maxPrice}€`;
    };

    const getStopSalesCount = () => {
        if (!product.activity?.pricing) return 0;
        return product.activity.pricing.filter(p => p.is_stop_sales).length;
    };

    const styles = {
        section: {
            background: 'white',
            borderRadius: '12px',
            padding: '12px 24px 24px',
            marginBottom: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        },
        sectionTitle: {
            fontSize: '20px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px'
        },
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
        },
        infoCard: {
            background: '#f8fafc',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
        },
        infoItem: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 0',
            borderBottom: '1px solid #f3f4f6'
        },
        infoLabel: {
            fontWeight: '600',
            color: '#374151',
            fontSize: '14px'
        },
        infoValue: {
            color: '#6b7280',
            fontSize: '14px',
            textAlign: 'right'
        },
        list: {
            listStyle: 'none',
            padding: 0,
            margin: 0
        },
        listItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 0',
            fontSize: '14px',
            color: '#374151'
        },
        scheduleGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
            marginTop: '16px'
        },
        scheduleCard: {
            background: '#f0f9ff',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #bae6fd'
        },
        languageTag: {
            display: 'inline-block',
            padding: '4px 8px',
            background: '#dbeafe',
            color: '#1e40af',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            margin: '2px'
        },
        iconText: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
        },
        twoColumn: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px'
        },
        pricingGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '12px',
            marginTop: '16px'
        },
        pricingCard: {
            background: '#f0fdf4',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #bbf7d0'
        },
        stopSalesCard: {
            background: '#fef2f2',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #fecaca'
        },
        pricingHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
        },
        pricingDate: {
            fontWeight: '600',
            color: '#1f2937'
        },
        pricingPrices: {
            display: 'flex',
            flexDirection: 'column',
            gap: '4px'
        },
        priceRow: {
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '13px'
        },
        priceLabel: {
            color: '#6b7280'
        },
        priceValue: {
            fontWeight: '600',
            color: '#1f2937'
        },
        badge: {
            display: 'inline-block',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '600',
            textTransform: 'uppercase',
            marginLeft: '8px'
        },
        availableBadge: {
            background: '#10b981',
            color: 'white'
        },
        stopSalesBadge: {
            background: '#ef4444',
            color: 'white'
        },
        sectionSubtitle: {
            fontSize: '16px',
            fontWeight: '500',
            color: '#6b7280',
            marginBottom: '16px'
        },
        statsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '16px',
            marginBottom: '20px'
        },
        statCard: {
            background: '#f8fafc',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
        },
        statValue: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#7c3aed',
            marginBottom: '4px'
        },
        statLabel: {
            fontSize: '12px',
            color: '#6b7280',
            textTransform: 'uppercase'
        }
    };

    const activity = product.activity;
    if (!activity) {
        return (
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>{t('activityDetails')}</h2>
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                    {t('noActivityDetails')}
                </p>
            </div>
        );
    }

    return (
        <>
            {/* Section Détails de l'activité */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>{t('activityDetails')}</h2>

                {/* Statistiques rapides */}
                <div style={styles.statsGrid}>
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{getPricingSummary()}</div>
                        <div style={styles.statLabel}>{t('priceRange')}</div>
                    </div>
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{product.activity?.pricing?.length || 0}</div>
                        <div style={styles.statLabel}>{t('pricing')}</div>
                    </div>
                    <div style={styles.statCard}>
                        <div style={styles.statValue}>{activity.schedules?.length || 0}</div>
                        <div style={styles.statLabel}>{t('schedules')}</div>
                    </div>
                </div>

                <div style={styles.grid}>
                    {/* Informations de base */}
                    <div style={styles.infoCard}>
                        <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('basicInfo')}</h3>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>{t('activityName')}</span>
                            <span style={styles.infoValue}>{getActivityName()}</span>
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>{t('activityType')}</span>
                            <span style={styles.infoValue}>{activity.activity_type_id || t('notSpecified')}</span>
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>{t('meetingPoint')}</span>
                            <span style={styles.infoValue}>{activity.meeting_point || t('notSpecified')}</span>
                        </div>
                        {activity.dropoff_location && (
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>{t('dropoffLocation')}</span>
                                <span style={styles.infoValue}>{activity.dropoff_location}</span>
                            </div>
                        )}
                    </div>

                    {/* Capacités */}
                    <div style={styles.infoCard}>
                        <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('capacity')}</h3>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>{t('adults')}</span>
                            <span style={styles.infoValue}>{activity.min_adults} - {activity.max_adults}</span>
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>{t('children')}</span>
                            <span style={styles.infoValue}>{activity.min_child} - {activity.max_child}</span>
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>{t('seniors')}</span>
                            <span style={styles.infoValue}>{activity.min_senior} - {activity.max_senior}</span>
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>{t('babies')}</span>
                            <span style={styles.infoValue}>{activity.min_baby} - {activity.max_baby}</span>
                        </div>
                    </div>

                    {/* Durée et planning */}
                    <div style={styles.infoCard}>
                        <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('durationTiming')}</h3>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>{t('duration')}</span>
                            <span style={styles.infoValue}>
                                {formatDuration(activity.min_duration_value, activity.min_duration_unit)}
                                {activity.max_duration_value > 0 && ` - ${formatDuration(activity.max_duration_value, activity.max_duration_unit)}`}
                            </span>
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>{t('cutoffTime')}</span>
                            <span style={styles.infoValue}>
                                {formatDuration(activity.cutoff_time_booking_value, activity.cutoff_time_booking_unit)}
                            </span>
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>{t('arrivalTime')}</span>
                            <span style={styles.infoValue}>
                                {formatDuration(activity.arrival_time_value, activity.arrival_time_unit)}
                            </span>
                        </div>
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>{t('dateRange')}</span>
                            <span style={styles.infoValue}>
                                {formatDate(activity.start_date)} - {formatDate(activity.end_date)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Options et Guide/Langues */}
                <div style={{ marginTop: '24px' }}>
                    <div style={styles.grid}>

                        {/* --- Colonne Options --- */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('options')}</h3>
                            <div style={styles.infoCard}>

                                <ul style={styles.list}>

                                    <li style={styles.listItem}>

                                        {activity.is_private_activity ? <Check color="#10b981" size={16} /> : <X color="#ef4444" size={16} />}

                                        {t('privateActivity')}

                                    </li>

                                    <li style={styles.listItem}>

                                        {activity.has_dropoff_location ? <Check color="#10b981" size={16} /> : <X color="#ef4444" size={16} />}

                                        {t('dropoffLocation')}

                                    </li>

                                    <li style={styles.listItem}>

                                        {activity.is_food_included ? <Check color="#10b981" size={16} /> : <X color="#ef4444" size={16} />}

                                        {t('foodIncluded')}

                                    </li>

                                    <li style={styles.listItem}>

                                        {activity.is_transport_used ? <Check color="#10b981" size={16} /> : <X color="#ef4444" size={16} />}

                                        {t('transportIncluded')}

                                    </li>

                                    <li style={styles.listItem}>

                                        {activity.are_drinks_included ? <Check color="#10b981" size={16} /> : <X color="#ef4444" size={16} />}

                                        {t('drinksIncluded')}

                                    </li>

                                </ul>

                            </div>
                        </div>

                        {/* --- Colonne Guide et Langues --- */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('guide')}</h3>
                            {activity.has_guide ? (
                                <div style={styles.grid}>
                                    {activity.guide_in_person && parseLanguages(activity.guide_in_person).length > 0 && (
                                        <div style={styles.infoCard}>
                                            <div style={{ marginBottom: '12px' }}>
                                                <strong>{t('guideInPerson')}: </strong>
                                                {parseLanguages(activity.guide_in_person).map(lang => (
                                                    <span key={lang} style={styles.languageTag}>{lang}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {activity.guide_audio && parseLanguages(activity.guide_audio).length > 0 && (
                                        <div style={styles.infoCard}>
                                            <div>
                                                <strong>{t('audioGuide')}: </strong>
                                                {parseLanguages(activity.guide_audio).map(lang => (
                                                    <span key={lang} style={styles.languageTag}>{lang}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '20px',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    color: '#6b7280',
                                    backgroundColor: '#f8fafc',
                                    height: '100%',
                                    minHeight: '20px'
                                }}>
                                    <X color="#9ca3af" size={24} style={{ marginBottom: '8px' }} />
                                    <p>{t('noGuideAvailable')}</p>
                                </div>
                            )}
                        </div>

                    </div>
                </div>

                {/* Horaires */}
                {activity.schedules && activity.schedules.length > 0 && (
                    <div style={{ marginTop: '24px' }}>
                        <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>{t('schedules')}</h3>
                        <div style={styles.scheduleGrid}>
                            {activity.schedules.map((schedule, index) => (
                                <div key={index} style={styles.scheduleCard}>
                                    <div style={styles.iconText}>
                                        <Clock size={16} />
                                        <strong>{t(schedule.day)}</strong>
                                    </div>
                                    <div>{schedule.start_time} - {schedule.end_time}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Section Tarification */}
            {activity.pricing && activity.pricing.length > 0 && (
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>
                        {t('pricing')}
                        <span style={{ ...styles.badge, ...(getStopSalesCount() > 0 ? styles.stopSalesBadge : styles.availableBadge) }}>
                            {getStopSalesCount() > 0
                                ? `${getStopSalesCount()} ${t('stopSales')}`
                                : t('allAvailable')}
                        </span>
                    </h2>

                    <div style={styles.pricingGrid}>
                        {activity.pricing.slice(0, 8).map((price, index) => (
                            <div
                                key={index}
                                style={price.is_stop_sales ? styles.stopSalesCard : styles.pricingCard}
                            >
                                <div style={styles.pricingHeader}>
                                    <div style={styles.pricingDate}>
                                        {formatDate(price.date_at)}
                                        {price.is_stop_sales && (
                                            <span style={{ ...styles.badge, ...styles.stopSalesBadge }}>
                                                {t('stopSales')}
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                        {t('available')}: {price.pax_available}
                                    </div>
                                </div>
                                <div style={styles.pricingPrices}>
                                    <div style={styles.priceRow}>
                                        <span style={styles.priceLabel}>{t('adult')}:</span>
                                        <span style={styles.priceValue}>{price.adult_price}€</span>
                                    </div>
                                    {price.child_price > 0 && (
                                        <div style={styles.priceRow}>
                                            <span style={styles.priceLabel}>{t('child')}:</span>
                                            <span style={styles.priceValue}>{price.child_price}€</span>
                                        </div>
                                    )}
                                    {price.senior_price > 0 && (
                                        <div style={styles.priceRow}>
                                            <span style={styles.priceLabel}>{t('senior')}:</span>
                                            <span style={styles.priceValue}>{price.senior_price}€</span>
                                        </div>
                                    )}
                                    {price.baby_price > 0 && (
                                        <div style={styles.priceRow}>
                                            <span style={styles.priceLabel}>{t('baby')}:</span>
                                            <span style={styles.priceValue}>{price.baby_price}€</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {activity.pricing.length > 8 && (
                        <p style={{ marginTop: '16px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                            {t('showingFirst')} 8 {t('of')} {activity.pricing.length} {t('pricingDates')}
                        </p>
                    )}
                </div>
            )}

            {/* Section Inclus/Exclus */}
            {(product.included && product.included.length > 0) ||
                (product.excluded && product.excluded.length > 0) ? (
                <div style={styles.section}>
                    {/* <h2 style={styles.sectionTitle}>{t('inclusionsExclusions')}</h2> */}
                    <div style={styles.twoColumn}>
                        {product.included && product.included.length > 0 && (
                            <div>
                                <h3 style={{ marginBottom: '12px', color: '#10b981' }}>{t('included')}</h3>
                                <ul style={styles.list}>
                                    {product.included.map((item, index) => (
                                        <li key={index} style={styles.listItem}>
                                            <Check color="#10b981" size={16} />
                                            {getFieldValue([item], `Item ${index + 1}`)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {product.excluded && product.excluded.length > 0 && (
                            <div>
                                <h3 style={{ marginBottom: '12px', color: '#ef4444' }}>{t('excluded')}</h3>
                                <ul style={styles.list}>
                                    {product.excluded.map((item, index) => (
                                        <li key={index} style={styles.listItem}>
                                            <X color="#ef4444" size={16} />
                                            {getFieldValue([item], `Item ${index + 1}`)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}

            {/* Section Éléments requis/interdits */}
            {(product.required_items && product.required_items.length > 0) ||
                (product.prohibited_items && product.prohibited_items.length > 0) ? (
                <div style={styles.section}>
                    {/* <h2 style={styles.sectionTitle}>{t('items')}</h2> */}
                    <div style={styles.twoColumn}>
                        {product.required_items && product.required_items.length > 0 && (
                            <div>
                                <h3 style={{ marginBottom: '12px', color: '#10b981' }}>{t('requiredItems')}</h3>
                                <ul style={styles.list}>
                                    {product.required_items.map((item, index) => (
                                        <li key={index} style={styles.listItem}>
                                            <Check color="#10b981" size={16} />
                                            {getFieldValue([item], `Item ${index + 1}`)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {product.prohibited_items && product.prohibited_items.length > 0 && (
                            <div>
                                <h3 style={{ marginBottom: '12px', color: '#ef4444' }}>{t('prohibitedItems')}</h3>
                                <ul style={styles.list}>
                                    {product.prohibited_items.map((item, index) => (
                                        <li key={index} style={styles.listItem}>
                                            <X color="#ef4444" size={16} />
                                            {getFieldValue([item], `Item ${index + 1}`)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default ActivityView;